import { useCallback, useEffect, useState } from "react";
import PortalLayout from "../../components/PortalLayout";
import { api, formatApiErrorDetail, formatDate } from "../../lib/api";

/**
 * Admin view of referral leads submitted via the public /referral/:code form.
 *
 * Actions:
 *   - Convert a lead into an invite (creates an invite tagged with the introducer id)
 *   - Mark a lead as closed (out of scope, spam, etc.)
 */
export default function AdminReferralLeadsPage() {
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");
  const [inviting, setInviting] = useState(null); // lead id being invited

  const load = useCallback(() => {
    const url = filter ? `/admin/referral-leads?status=${filter}` : "/admin/referral-leads";
    return api
      .get(url)
      .then(({ data }) => setLeads(data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const invite = async (lead) => {
    if (!window.confirm(`Creare un invito per ${lead.email}?`)) return;
    setInviting(lead.id);
    setError("");
    try {
      await api.post("/admin/invites", {
        email: lead.email,
        name: lead.name,
        role: "client",
        locale: lead.locale || "it",
        referred_by_introducer_code: lead.introducer_code,
        referral_lead_id: lead.id,
      });
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setInviting(null);
    }
  };

  const setStatus = async (lead, newStatus) => {
    try {
      await api.patch(`/admin/referral-leads/${lead.id}`, { status: newStatus });
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const statusPill = (s) => {
    const map = {
      new: { label: "Nuovo", color: "var(--br-bronze)" },
      in_review: { label: "In esame", color: "var(--br-bronze-soft)" },
      invited: { label: "Invitato", color: "var(--br-bronze-soft)" },
      converted: { label: "Convertito", color: "#7fbf7f" },
      closed: { label: "Chiuso", color: "var(--br-text-dim)" },
    };
    const cfg = map[s] || { label: s, color: "var(--br-text-dim)" };
    return (
      <span className="font-mono-fin text-[0.62rem] tracking-[0.16em] uppercase px-2 py-1" style={{ color: cfg.color, border: `1px solid ${cfg.color}` }}>
        {cfg.label}
      </span>
    );
  };

  return (
    <PortalLayout>
      <section className="max-w-[1280px] mx-auto px-6 sm:px-8 pt-14 pb-8" data-testid="admin-referral-leads-page">
        <div className="br-fade-up">
          <div className="br-overline">Lead Referral</div>
          <h1 className="font-serif-display text-[clamp(2.4rem,4.5vw,3.6rem)] mt-3">
            Contatti dai <em>partner</em>.
          </h1>
          <p className="text-[1.05rem] text-[color:var(--br-text)] mt-3 max-w-[720px]">
            Prospetti arrivati dai form pubblici <span className="font-mono-fin text-[0.85rem]">/referral/:codice</span>. Convertili in inviti per aprire l'onboarding.
          </p>
        </div>

        {error && <div className="mt-6 br-card p-4 text-[color:var(--br-red)]" data-testid="leads-err">{error}</div>}

        <div className="mt-8 flex flex-wrap gap-2" data-testid="leads-filters">
          {[
            { v: "", label: "Tutti" },
            { v: "new", label: "Nuovi" },
            { v: "invited", label: "Invitati" },
            { v: "converted", label: "Convertiti" },
            { v: "closed", label: "Chiusi" },
          ].map((f) => (
            <button
              key={f.v || "all"}
              onClick={() => setFilter(f.v)}
              className="br-btn-outline"
              style={{
                padding: "6px 14px",
                fontSize: "0.72rem",
                background: filter === f.v ? "rgba(196,155,102,0.12)" : "transparent",
              }}
              data-testid={`leads-filter-${f.v || "all"}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-6 br-card overflow-x-auto br-fade-up br-delay-1" data-testid="leads-table-wrap">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--br-line)" }}>
                <Th>Contatto</Th>
                <Th>Introducer</Th>
                <Th>Ricevuto</Th>
                <Th>Stato</Th>
                <Th align="right">Azioni</Th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-[color:var(--br-text-dim)]">
                    Nessun lead trovato.
                  </td>
                </tr>
              ) : (
                leads.map((l) => (
                  <tr key={l.id} className="border-b" style={{ borderColor: "var(--br-line)" }} data-testid={`lead-row-${l.id}`}>
                    <Td>
                      <div className="text-[color:var(--br-parchment)] font-serif-display italic text-[1.05rem]">{l.name}</div>
                      <div className="text-[0.72rem] text-[color:var(--br-text-dim)] font-mono-fin">{l.email}</div>
                      {l.phone && <div className="text-[0.72rem] text-[color:var(--br-text-dim)] font-mono-fin">{l.phone}</div>}
                      {l.message && <div className="text-[0.78rem] text-[color:var(--br-text)] mt-1 italic">"{l.message}"</div>}
                    </Td>
                    <Td>
                      <div className="font-mono-fin text-[0.75rem]" style={{ color: "var(--br-bronze)" }}>{l.introducer_code}</div>
                      <div className="text-[0.7rem] text-[color:var(--br-text-dim)]">{l.introducer_name}</div>
                    </Td>
                    <Td className="font-mono-fin text-[0.75rem] text-[color:var(--br-text-dim)]">
                      {formatDate(l.created_at)}
                    </Td>
                    <Td>{statusPill(l.status)}</Td>
                    <Td align="right">
                      <div className="flex gap-2 justify-end">
                        {(l.status === "new" || l.status === "in_review") && (
                          <button
                            onClick={() => invite(l)}
                            disabled={inviting === l.id}
                            className="br-btn-outline"
                            style={{ padding: "6px 10px", fontSize: "0.72rem", color: "var(--br-bronze-soft)", borderColor: "var(--br-bronze-soft)" }}
                            data-testid={`lead-invite-${l.id}`}
                          >
                            {inviting === l.id ? "Invio…" : "Invita"}
                          </button>
                        )}
                        {l.status !== "closed" && l.status !== "converted" && (
                          <button
                            onClick={() => setStatus(l, "closed")}
                            className="br-btn-outline"
                            style={{ padding: "6px 10px", fontSize: "0.72rem", color: "var(--br-red)", borderColor: "var(--br-red)" }}
                            data-testid={`lead-close-${l.id}`}
                          >
                            Chiudi
                          </button>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </PortalLayout>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th
      className="px-5 py-4 font-mono-fin text-[0.62rem] tracking-[0.22em] uppercase font-medium"
      style={{ color: "var(--br-text-dim)", textAlign: align }}
    >
      {children}
    </th>
  );
}

function Td({ children, align = "left", className = "", style }) {
  return (
    <td className={`px-5 py-5 text-[0.9rem] ${className}`} style={{ textAlign: align, ...style }}>
      {children}
    </td>
  );
}
